import { Swiper } from "swiper";
import { Autoplay, Grid, Navigation, Pagination } from "swiper/modules";
import { SwiperOptions } from "swiper/types";
import { breakpoints } from "../../breakpoints.ts";

export class CustomSwiper extends Swiper {
	public constructor(el: HTMLElement, config: SwiperOptions = {}) {
		const elConfig = CustomSwiper.#getElConfig(el.dataset, matchMedia("(prefers-reduced-motion: reduce)").matches);
		super(el, {
			...elConfig,
			...config,
			// merge modules from both configs
			modules: [
				...(elConfig.modules || []),
				...(config.modules || []),
			]
		});
	}

	/**
	 *
	 */
	static #getElConfig(config: DOMStringMap, prefersReducedMotion = false): SwiperOptions {
		// read configuration values from element's data- attributes
		const slidesPerView = config["slidesPerView"] === "auto" ? "auto" : (Number(config["slidesPerView"]) || 1);
		const slidesPerGroup = Number(config["slidesPerGroup"]) || 1;

		let spaceBetween = Number(config["spaceBetween"]);
		if (isNaN(spaceBetween)) {
			spaceBetween = 16;
		}
		const numGridRows = Number(config["gridRows"]) || 1;
		const speed = prefersReducedMotion ? 0 : (Number(config["speed"]) || 300);

		//const isLazy = "lazy" in config;
		const isLoop = "loop" in config;
		const isNavigation = "navigation" in config;
		const isPagination = "pagination" in config;
		const isAutoplay = "autoplay" in config;
		const autoplayDelay = Number(config["autoplayDelay"]) || 3000;

		// identify breakpoint-dependent values
		const swiperBreakpoints: SwiperOptions["breakpoints"] = {};
		for (const [key, size] of breakpoints) {
			// breakpoint.slidesPerView
			if (`${key}SlidesPerView` in config) {
				const slidesPerView = config[`${key}SlidesPerView`] || "auto";
				swiperBreakpoints[size * 16] = {
					...(swiperBreakpoints[size * 16] || {}),
					slidesPerView: slidesPerView === "auto" ? "auto" : parseInt(slidesPerView, 10) || 1
				};
			}

			// breakpoint.slidesPerGroup
			if (`${key}SlidesPerGroup` in config) {
				const slidesPerGroup = config[`${key}SlidesPerGroup`];
				swiperBreakpoints[size * 16] = {
					...(swiperBreakpoints[size * 16] || {}),
					slidesPerGroup: Number(slidesPerGroup) || 0,
				};
			}

			// breakpoint.spaceBetween
			if (`${key}SpaceBetween` in config) {
				const spaceBetween = config[`${key}SpaceBetween`];
				swiperBreakpoints[size * 16] = {
					...(swiperBreakpoints[size * 16] || {}),
					spaceBetween: Number(spaceBetween) || 0,
				};
			}
		}

		return {
			modules: [
				...(numGridRows > 1 ? [Grid] : []),     // conditionally include 'Grid' module
				//...(isLazy ? [Lazy] : []),              // conditionally include 'Lazy' module
				...(isNavigation ? [Navigation] : []),  // conditionally include 'Navigation' module
				...(isPagination ? [Pagination] : []),  // conditionally include 'Pagination' module
				...(isAutoplay ? [Autoplay] : []),      // conditionally include 'Autoplay' module
			],
			watchSlidesProgress: slidesPerView === "auto" || slidesPerView > 1, // load any visible slides (Lazy + Grid doesn't load the correct number of images at startup)
			slidesPerView,
			slidesPerGroup,
			spaceBetween,
			speed,
			//lazy: isLazy,
			loop: isLoop,
			breakpoints: swiperBreakpoints,

			...(numGridRows > 1 && {
				grid: {
					rows: numGridRows,
					fill: "row",
				},
			}),

			...(isNavigation && {
				navigation: {
					prevEl: ".swiper-button-prev",
					nextEl: ".swiper-button-next",
				},
			}),

			...(isPagination && {
				pagination: {
					el: ".swiper-pagination",
					clickable: true,
				},
			}),

			...(isAutoplay && (() => {
				const autoplayConfig: { autoplay?: { delay: number } } = {};
				if (matchMedia("(prefers-reduced-motion: no-preference)").matches) {
					autoplayConfig.autoplay = { delay: autoplayDelay };
				}

				return autoplayConfig;
			})()),
		};
	}
}
